import {
  Badge,
  Card,
  Divider,
  Grid,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import type { DescriptiveStatRow } from '../utils/computeDescriptiveStatistics';
import { formatStatValue } from '../utils/formatStatValue';
import { StatHelp } from './StatHelp';

type DescriptiveStatCardProps = {
  row: DescriptiveStatRow;
  segmentLabel: string;
};

export function DescriptiveStatCard({
  row,
  segmentLabel,
}: DescriptiveStatCardProps) {
  return (
    <Card withBorder h='100%'>
      <Stack gap='sm'>
        <Group justify='space-between' align='flex-start'>
          <div>
            <Title order={5}>{row.feature}</Title>
            <Text size='sm' c='dimmed'>
              {row.sessionCount} sessioni considerate nel segmento.
            </Text>
          </div>
          <Badge variant='light'>{segmentLabel}</Badge>
        </Group>

        <Grid>
          <Grid.Col span={6}>
            <StatHelp
              label='Media gruppo'
              description='Media dei valori medi calcolati per ogni sessione nel segmento selezionato.'
            />
            <Text fw={700}>{formatStatValue(row.mean)}</Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <StatHelp
              label='Variabilità'
              description='Deviazione standard tra le sessioni: più è alta, più il gruppo è eterogeneo.'
            />
            <Text fw={700}>{formatStatValue(row.std)}</Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <StatHelp
              label='Range'
              description='Valore minimo e massimo osservati tra le medie delle sessioni.'
            />
            <Text fw={700}>
              {formatStatValue(row.min)} / {formatStatValue(row.max)}
            </Text>
          </Grid.Col>
          <Grid.Col span={6}>
            <StatHelp
              label='Coeff. variabilità'
              description='Rapporto percentuale tra deviazione standard e media: aiuta a capire quanto il gruppo varia rispetto al proprio livello medio.'
            />
            <Text fw={700}>
              {formatStatValue(row.coefficientOfVariation, '%')}
            </Text>
          </Grid.Col>
        </Grid>

        <Divider />
        <Stack gap={4}>
          <StatHelp
            label='Sessioni più distanti'
            description='Sessioni con valore medio più lontano dalla media del gruppo nel segmento selezionato.'
          />
          {row.outlierSessions.length === 0 ? (
            <Text size='sm' c='dimmed'>
              Nessuna sessione disponibile per questo segmento.
            </Text>
          ) : (
            row.outlierSessions.map(session => (
              <Text key={`${row.feature}-${session.sessionId}`} size='sm'>
                Sessione {session.sessionId}
                {session.patientCode ? ` · ${session.patientCode}` : ''}: valore{' '}
                {formatStatValue(session.value)}, distanza{' '}
                {formatStatValue(session.distance)}
              </Text>
            ))
          )}
        </Stack>
      </Stack>
    </Card>
  );
}
